'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { CheckCircle2 } from 'lucide-react';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { cn } from '@/lib/utils';

interface Plan {
    name: string;
    features?: string[];
}

interface FeaturesSectionProps {
    plan: Plan;
    translationNamespace: string;
}

export function FeaturesSection({ plan, translationNamespace }: FeaturesSectionProps) {
    const t = useTranslations(translationNamespace);
    const sectionRef = useScrollAnimation({ threshold: 0.15 });

    const features = useMemo(() => {
        const items: string[] = [];
        for (let i = 0; i < 20; i++) {
            const key = `items.${i}` as Parameters<typeof t.has>[0];
            if (!t.has(key)) break;
            items.push(t(key));
        }
        return items;
    }, [t]);

    return (
        <SectionWrapper ref={sectionRef.ref} variant="default" padding="lg" className="relative bg-white dark:bg-gray-950">
            <div className="container">
                <div className={cn(
                    "mb-12 transition-all duration-700",
                    sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                )}>
                    <Typography variant="h2" className="mb-4">
                        {t('title')}
                    </Typography>
                    <Typography variant="p" className="text-gray-600 dark:text-gray-400 max-w-4xl leading-relaxed">
                        {t('description')}
                    </Typography>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {features.map((feature: string, index: number) => (
                        <div
                            key={index}
                            className={cn(
                                "bg-gray-100 dark:bg-gray-800 rounded-xl p-5 flex items-center gap-4 transition-all duration-700",
                                sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                            )}
                            style={{ transitionDelay: `${200 + index * 50}ms` }}
                        >
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500 dark:bg-blue-600 flex items-center justify-center">
                                <CheckCircle2 className="w-6 h-6 text-white" />
                            </div>
                            <Typography variant="p" className="text-gray-700 dark:text-gray-300 leading-relaxed flex-1">
                                {feature}
                            </Typography>
                        </div>
                    ))}
                </div>
            </div>
        </SectionWrapper>
    );
}

